"use client";

import { useMutation, useQuery } from "@apollo/client";
import { BriefcaseBusiness, CalendarCheck, CalendarDays, CheckCircle2, ChevronDown, ChevronUp, Clock3, PauseCircle, Pencil, PlayCircle, PlusCircle, Archive } from "lucide-react";
import moment from "moment";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { gqlUser } from "gql";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import {
  Card,
  Input,
  LiquidPill,
  Modal,
  ModalButtons,
  PageEmptyStateV2,
  PageLoadingStateV2,
} from "components/ui/v2";
import { RichTextEditorV2 } from "components/ui/v2/rich-text-editor-v2";
import type { CircleActionVariant } from "components/ui/v2/circle-action-variants";
import { IVendorProject } from "types/Project.type";
import { getQueryOperator } from "utils/helpers";
import { ResumeTabHeaderV2 } from "./ResumeTabHeaderV2";

function getProjectStatusVariant(status: string): CircleActionVariant {
  if (status === "ACTIVE") return "emerald";
  if (status === "PAUSED") return "gold";
  if (status === "NEW") return "ocean";
  if (status === "OLD") return "violet";
  return "slate";
}

function getBusinessUnitVariant(businessUnit: string): CircleActionVariant {
  switch (businessUnit) {
    case "CSP": return "indigo";
    case "FIX_PROJECT": return "rose";
    case "HUNTING": return "sunset";
    case "LICENCES": return "emerald";
    case "TIME_AND_MATERIALS": return "cyan";
    case "STAFFING": return "violet";
    case "SUPPORT": return "fuchsia";
    case "SUBSCRIPTION": return "mint";
    default: return "slate";
  }
}

function getStatusIcon(status: string) {
  if (status === "ACTIVE") return <PlayCircle className="h-3 w-3" />;
  if (status === "PAUSED") return <PauseCircle className="h-3 w-3" />;
  if (status === "NEW") return <PlusCircle className="h-3 w-3" />;
  if (status === "OLD") return <Archive className="h-3 w-3" />;
  return <CheckCircle2 className="h-3 w-3" />;
}

function projectBusinessUnitLabel(businessUnit: string) {
  if (businessUnit === "FIX_PROJECT") return "FIX PROJECT";
  if (businessUnit === "TIME_AND_MATERIALS") return "T&M";
  return businessUnit || "OTHER";
}

export const RESUME_PROJECT_UPDATE_FORM_ID = "resume-project-update-form";

function ProjectUpdateModalContentV2({
  project,
  onClose,
  hideFooter = false,
  onFooterChange,
  formId = RESUME_PROJECT_UPDATE_FORM_ID,
}: {
  project: IVendorProject;
  onClose: () => void;
  hideFooter?: boolean;
  onFooterChange?: (footer: ReactNode) => void;
  formId?: string;
}) {
  const onFooterChangeRef = useRef(onFooterChange);
  onFooterChangeRef.current = onFooterChange;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const { t } = useTranslation("common");
  const { user } = useUserStoreContext((store) => ({ user: store.user }));
  const friendlyName = project.projectFriendlyName || project.name || "";
  const [editorValue, setEditorValue] = useState((project as any).description || "");
  const { control, handleSubmit, setValue } = useForm({
    values: {
      name: project.name || "",
      projectFriendlyName: friendlyName,
      description: (project as any).description || "",
    },
  });

  useEffect(() => {
    setEditorValue((project as any).description || "");
  }, [project]);

  const [updateProject, { loading }] = useMutation(gqlUser.mutations.UPDATE_ASSIGNMENT_PROJECT, {
    refetchQueries: user?.id
      ? [{ query: gqlUser.queries.USER_PROJECTS, variables: { userId: user.id } }]
      : [getQueryOperator(gqlUser.queries.USER_PROJECTS)],
    awaitRefetchQueries: true,
    onCompleted() {
      toast.success(t("Proyecto actualizado con éxito"));
      onCloseRef.current?.();
    },
    onError() {
      toast.error(t("Error al actualizar el proyecto"));
    },
  });

  const onSubmit = (data: any) => {
    updateProject({
      variables: {
        updateAssignmentProjectInput: {
          externalProjectId: (project as any).externalId,
          projectFriendlyName: data.projectFriendlyName || friendlyName,
          projectDescription: data.description,
        },
      },
    });
  };

  useLayoutEffect(() => {
    const publish = onFooterChangeRef.current;
    if (!hideFooter || !publish) return;

    publish(
      <ModalButtons
        compact
        className="!pt-0"
        onCancel={() => onCloseRef.current?.()}
        loading={loading}
        submitLabel={t("Actualizar")}
        cancelLabel={t("Cancelar")}
        submitButtonProps={{ form: formId, type: "submit" }}
      />
    );
    // Do not depend on `onClose` — a new function identity each render
    // would republish the footer forever (Maximum update depth exceeded).
  }, [formId, hideFooter, loading, t]);

  useEffect(() => {
    return () => {
      onFooterChangeRef.current?.(null);
    };
  }, []);

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Controller
        name="name"
        control={control}
        render={({ field }) => <Input {...field} label={t("Nombre del Proyecto")} disabled />}
      />
      <Controller
        name="projectFriendlyName"
        control={control}
        render={({ field }) => <Input {...field} label={t("Nombre Amigable del Proyecto")} placeholder={t("Ingresa el nombre amigable del proyecto")} />}
      />
      <div>
        <p className="mb-2 text-xs font-medium text-slate-600 dark:text-zinc-400">{t("Descripción del Proyecto")}</p>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <RichTextEditorV2
              value={editorValue}
              onChange={(html) => {
                setEditorValue(html);
                setValue("description", html);
                field.onChange(html);
              }}
              placeholder={t("Describe el proyecto…")}
              minHeight={320}
            />
          )}
        />
      </div>
      {!hideFooter ? (
        <ModalButtons
          onCancel={() => onCloseRef.current?.()}
          loading={loading}
          submitLabel={t("Actualizar")}
          cancelLabel={t("Cancelar")}
        />
      ) : null}
    </form>
  );
}

function ProjectCardV2({ project, canEdit, onEdit }: { project: IVendorProject; canEdit: boolean; onEdit: (project: IVendorProject) => void }) {
  const { t } = useTranslation("common");
  const status = project.status;
  const duration = (() => {
    if (!project.startDate) return null;
    const start = moment(project.startDate);
    const end = moment(project.realEndDate || project.endDate);
    const now = moment();
    const months = status === "ACTIVE" ? now.diff(start, "months") : end.diff(start, "months");
    return months > 0 ? `${months} ${months === 1 ? "mes" : "meses"}` : t("Menos de 1 mes");
  })();

  const statusLabel = status === "ACTIVE" ? t("Activo") : status === "PAUSED" ? t("Pausado") : status === "NEW" ? t("Nuevo") : status === "OLD" ? t("Antiguo") : t("Cerrado");

  return (
    <Card size="full" variant="default" padding="none" shadow="subtle" fullHeight entranceAnimation>
      <article className="flex h-full min-h-[220px] flex-col overflow-hidden">
        <div className="flex flex-1 flex-col p-3 text-left">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="line-clamp-1 text-base font-semibold leading-tight text-slate-950 dark:text-white">
                {(project as any).vendorClientName || "CleverIT"}
              </h3>
              <BriefcaseBusiness className="h-3.5 w-3.5 shrink-0 text-indigo-500 dark:text-indigo-300" />
            </div>
            <p className="mt-1 line-clamp-2 text-xs font-medium leading-snug text-slate-500 dark:text-zinc-400">
              {project.projectFriendlyName || project.name || "CleverIT"}
            </p>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <LiquidPill as="span" variant={getProjectStatusVariant(status)} appearance="tinted" shadow="neutral" size="xs" icon={getStatusIcon(status)} text={`${statusLabel}${(project as any).asignationPercentage ? ` ${(project as any).asignationPercentage}%` : ""}`} />
            <LiquidPill as="span" variant={getBusinessUnitVariant((project as any).businessUnit)} appearance="tinted" shadow="neutral" size="xs" text={projectBusinessUnitLabel((project as any).businessUnit)} />
            {duration ? <LiquidPill as="span" variant="slate" appearance="tinted" shadow="neutral" size="xs" icon={<Clock3 className="h-3 w-3" />} text={duration} /> : null}
          </div>

          {(project as any).description ? (
            <div className="mt-3 line-clamp-3 text-sm text-slate-600 dark:text-zinc-400" dangerouslySetInnerHTML={{ __html: (project as any).description }} />
          ) : null}

          <div className="mt-auto">
            <div className="mb-1 mt-3 flex flex-wrap items-center justify-center gap-x-1 gap-y-0.5 text-[10px] leading-none text-slate-500 dark:text-zinc-400">
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="h-2.5 w-2.5 text-slate-400" />
                {t("Desde")} <span className="font-medium text-slate-600 dark:text-zinc-300">{project.startDate ? moment(project.startDate).format("DD/MM/YYYY") : t("Sin fecha")}</span>
              </span>
              <span className="px-0.5 text-slate-300 dark:text-zinc-700">·</span>
              <span className="inline-flex items-center gap-1">
                <CalendarCheck className="h-2.5 w-2.5 text-slate-400" />
                {t("Hasta")} <span className="font-medium text-slate-600 dark:text-zinc-300">{project.realEndDate || project.endDate ? moment(project.realEndDate || project.endDate).format("DD/MM/YYYY") : status === "ACTIVE" ? t("En curso") : t("Sin fecha")}</span>
              </span>
            </div>

            {canEdit ? (
              <div className="border-t border-slate-200/70 pt-2.5 dark:border-zinc-800/80">
                <LiquidPill as="button" type="button" variant="indigo" appearance="tinted" shadow="neutral" size="card-cta" icon={<Pencil className="h-3.5 w-3.5" />} text={t("Actualizar")} onClick={() => onEdit(project)} />
              </div>
            ) : null}
          </div>
        </div>
      </article>
    </Card>
  );
}

export function ResumeProjectsTabV2() {
  const { t } = useTranslation("common");
  const [visibleProjects, setVisibleProjects] = useState(5);
  const [editingProject, setEditingProject] = useState<IVendorProject | null>(null);
  const [projectFooter, setProjectFooter] = useState<ReactNode>(null);
  const { user } = useUserStoreContext((store) => ({ user: store.user }));

  const { data, loading } = useQuery(gqlUser.queries.USER_PROJECTS, {
    fetchPolicy: "network-only",
    skip: !user?.id,
    variables: { userId: user?.id },
  });

  const activeProjects = data?.projectsByUser?.filter((project: IVendorProject) => project.status === "ACTIVE") || [];
  const otherProjects = data?.projectsByUser?.filter((project: IVendorProject) => project.status !== "ACTIVE") || [];
  otherProjects.sort((a: IVendorProject, b: IVendorProject) => new Date(b.startDate ?? 0).getTime() - new Date(a.startDate ?? 0).getTime());
  const projects = activeProjects.concat(otherProjects);

  return (
    <div className="grid gap-4">
      <ResumeTabHeaderV2
        title={t("Proyectos")}
        description={t("Los proyectos en los que participas o participaste")}
      />

      {loading ? (
        <PageLoadingStateV2 title={t("Cargando")} description={t("Proyectos")} />
      ) : projects.length === 0 ? (
        <PageEmptyStateV2
          variant="empty"
          icon={<BriefcaseBusiness className="h-8 w-8" />}
          title={t("No ha participado en proyectos aún")}
          description={t("Aquí aparecerán los proyectos en los que participes.")}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {projects.slice(0, visibleProjects).map((project: IVendorProject) => (
              <ProjectCardV2 key={project.id} project={project} canEdit onEdit={setEditingProject} />
            ))}
          </div>
          {projects.length > 5 ? (
            <div className="flex items-center justify-center gap-2">
              {projects.length > visibleProjects ? (
                <LiquidPill as="button" type="button" variant="slate" appearance="tinted" shadow="neutral" size="sm" icon={<ChevronDown className="h-4 w-4" />} text={t("Ver más")} onClick={() => setVisibleProjects(Math.min(visibleProjects + 5, projects.length))} />
              ) : null}
              {visibleProjects > 5 ? (
                <LiquidPill as="button" type="button" variant="slate" appearance="tinted" shadow="neutral" size="sm" icon={<ChevronUp className="h-4 w-4" />} text={t("Ver menos")} onClick={() => setVisibleProjects(5)} />
              ) : null}
            </div>
          ) : null}
        </>
      )}

      <Modal
        open={!!editingProject}
        onClose={() => {
          setEditingProject(null);
          setProjectFooter(null);
        }}
        title={t("Actualizar Proyecto")}
        description={t("Actualiza la información del proyecto")}
        size="lg"
        panelClassName="max-h-[min(88vh,760px)]"
        footer={projectFooter}
      >
        {editingProject ? (
          <ProjectUpdateModalContentV2
            project={editingProject}
            onClose={() => {
              setEditingProject(null);
              setProjectFooter(null);
            }}
            hideFooter
            onFooterChange={setProjectFooter}
          />
        ) : null}
      </Modal>
    </div>
  );
}
